/**
 * The one Branch Web SDK seam for the web client.
 *
 * Branch is here for two jobs, and deliberately not for a third:
 *
 *  1. Deferred deep linking. A member scans the QR on a companion-download
 *     surface, installs the app, and the first launch knows which surface sent
 *     them. Neither store URL can carry that, so without Branch the handoff
 *     from web to mobile loses its context every time.
 *  2. Install attribution. Which download surface actually produces installs
 *     is currently unanswerable: `companion_prompt_selected` records the tap
 *     and the trail ends at the App Store.
 *
 * It is NOT a product analytics sink. Segment owns the warehouse taxonomy and
 * Datadog RUM owns the funnels (see `analytics.ts`); Branch receives the tiny
 * install-intent allowlist in `analytics-branch.ts` and nothing else.
 *
 * ## Why no `setIdentity`
 *
 * Branch's `setIdentity` exists to join their identity graph to ours by
 * pushing a member id across. HealthOS is a Covered Entity surface and a
 * member id is a HIPAA identifier ("any other unique identifying number,
 * characteristic, or code"), so that call needs a signed BAA behind it before
 * it can exist. It is not needed for either job above: Branch matches a click
 * to an install with its own click id, with no member identifier involved. The
 * adapter's `identify` is therefore a documented no-op, not an oversight. Do
 * not add `setIdentity` without a BAA and a privacy review.
 *
 * Every export here is safe to call with no key configured (local dev, tests,
 * any deployment that has not been given one): the SDK is never even fetched
 * and each call resolves to a no-op. Nothing in this module rejects or throws,
 * because an attribution vendor being unreachable must never be able to break
 * a boot or a download surface.
 */

/**
 * The slice of the Branch Web SDK we use, typed by hand.
 *
 * `branch-sdk` ships a single minified UMD bundle with no type declarations.
 * `@types/branch-sdk` exists but trails the runtime version, and this mirrors
 * what `analytics.ts` already does for the Segment browser client: declare the
 * handful of methods we call, so a typo is a compile error and an upgrade
 * cannot silently widen the surface we depend on.
 */
interface BranchClient {
  init(key: string, options: BranchInitOptions, callback: BranchCallback<BranchSessionData>): void;
  link(payload: BranchLinkPayload, callback: BranchCallback<string>): void;
  logEvent(name: string, data: Record<string, unknown>, callback?: BranchCallback<unknown>): void;
  logout(callback?: BranchCallback<unknown>): void;
}

type BranchCallback<T> = (err: unknown, result?: T | null) => void;

interface BranchInitOptions {
  /** Suppresses the Journeys smart banner. See `BRANCH_INIT_OPTIONS`. */
  no_journeys: boolean;
}

/** The `data` argument Branch hands back from `init`. Only the fields we read. */
interface BranchSessionData {
  /** Custom data from the link that referred this session, already JSON-parsed. */
  data_parsed?: Record<string, unknown> | null;
}

export interface BranchLinkPayload {
  /** Attribution channel. Always our own constant, never member-derived. */
  channel: string;
  feature: string;
  campaign?: string;
  tags?: readonly string[];
  /** Link control parameters (`$ios_url`, `$deeplink_path`, ...) plus our own. */
  data: Record<string, unknown>;
}

/**
 * Journeys is Branch's interstitial "get the app" banner. It is off because it
 * renders its own chrome over ours, and HealthOS already owns that moment with
 * CompanionDownloadModal: two competing app-install prompts on one page is a
 * worse surface than either alone. It is also a paid add-on we do not hold.
 */
const BRANCH_INIT_OPTIONS: BranchInitOptions = { no_journeys: true };

/**
 * Link data keys Branch reads to decide where a click lands. A referring link
 * may set `$deeplink_path`; we also accept our own `healthos_path` so a link
 * minted by hand in the Branch dashboard does not have to know Branch's
 * reserved vocabulary.
 */
const DEEP_LINK_KEYS = ["$deeplink_path", "healthos_path"] as const;

/** A pathological value must not become an unbounded navigation target. */
const MAX_DEEP_LINK_LENGTH = 512;

/**
 * Query parameters Branch appends to the destination when it forwards a click.
 *
 * The presence of one is how we know THIS page load is a referral. Branch
 * persists its session in localStorage and hands the stored `data_parsed`
 * back to every later `init`, which is correct for attribution (the install
 * still belongs to that click) and wrong for routing: without this check the
 * first attributed visit turns into a deep-link path that replays on every
 * refresh for the rest of the session, so a member who reloads Today gets
 * bounced to whichever surface they arrived on days ago.
 *
 * The hop navigates with `replace` to a path with no query string, so the
 * marker is gone immediately afterwards and reloading the destination does
 * not re-trigger it either.
 */
const BRANCH_REFERRAL_PARAMS = ["_branch_match_id", "_branch_referrer"] as const;

/**
 * Whether this page load arrived through a Branch link.
 *
 * Exported for the tests. Takes the search string rather than reading
 * `window.location` so it is testable and so the caller controls when it is
 * sampled: `initBranch` runs in `boot()` before the router mounts, which is
 * the only moment the landing URL is still intact (see `applyEmailAttribution`
 * for the same constraint).
 */
export function isBranchReferredLoad(search: string): boolean {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return false;
  }
  return BRANCH_REFERRAL_PARAMS.some((param) => params.has(param));
}

type BranchState = {
  client: BranchClient;
  /**
   * Resolved once from the init session. Null unless this page load actually
   * came from a Branch link (see `BRANCH_REFERRAL_PARAMS`).
   */
  deepLinkPath: string | null;
};

let statePromise: Promise<BranchState | null> | null = null;
let resolvedState: BranchState | null = null;

/**
 * Normalise a link's requested destination into a path this SPA may navigate to.
 *
 * Branch link data is attacker-controllable: anyone who can mint a link on our
 * key (or craft a `?_branch_match_id` visit) chooses this value, and it is fed
 * straight to the router. Without this, `$deeplink_path: "https://evil.test"`
 * or `"//evil.test"` is an open redirect on an authenticated health surface.
 * So the only accepted shape is a single-slash, same-origin relative path.
 *
 * Exported for the tests, which is the whole point of pinning this in one place.
 */
export function safeDeepLinkPath(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > MAX_DEEP_LINK_LENGTH) return null;
  // Protocol-relative ("//host") and scheme-relative ("/\host", which several
  // browsers normalise to "//host") both leave our origin.
  if (!trimmed.startsWith("/") || trimmed.startsWith("//") || trimmed.startsWith("/\\")) {
    return null;
  }
  if (trimmed.includes("\\")) return null;
  // Control characters can be used to smuggle a scheme past the checks above.
  // biome-ignore lint/suspicious/noControlCharactersInRegex: rejecting them is the point
  if (/[\u0000-\u001f\u007f]/.test(trimmed)) return null;
  return trimmed;
}

function readDeepLinkPath(data: BranchSessionData | null | undefined): string | null {
  const parsed = data?.data_parsed;
  if (!parsed) return null;
  for (const key of DEEP_LINK_KEYS) {
    const path = safeDeepLinkPath(parsed[key]);
    if (path) return path;
  }
  return null;
}

/**
 * Load and initialise the SDK. Resolves to null when Branch is not configured.
 *
 * Dynamically imported so the 79kB bundle never lands in the main chunk: it is
 * needed on a download surface and on a link-referred visit, not on the
 * critical path to first paint. Called once from `boot()`; the promise is
 * cached so the five download surfaces share one session rather than racing
 * five inits.
 */
export function initBranch(key: string | undefined): Promise<BranchState | null> {
  if (!key) return Promise.resolve(null);
  if (statePromise) return statePromise;

  statePromise = import("branch-sdk")
    .then((mod) => {
      // The bundle is UMD: `module.exports = branch` under Vite's CJS interop,
      // which surfaces as `default`. Falling through to the namespace keeps
      // this working if the package ever ships a real ES module.
      const client = ((mod as { default?: BranchClient }).default ??
        (mod as unknown as BranchClient)) as BranchClient;
      return new Promise<BranchState | null>((resolve) => {
        client.init(key, BRANCH_INIT_OPTIONS, (err, data) => {
          if (err) {
            // Warn, not error: attribution is degraded, the product is not.
            console.warn("branch_init_failed", describe(err));
            resolve(null);
            return;
          }
          /*
           * Sampled here rather than inside `readDeepLinkPath` because this
           * callback is the earliest point after boot and the router has not
           * mounted yet, so `location.search` is still the landing URL.
           */
          const referred = isBranchReferredLoad(window.location.search);
          resolve({ client, deepLinkPath: referred ? readDeepLinkPath(data) : null });
        });
      });
    })
    .catch((err) => {
      // A blocked or failed chunk fetch. Common in the wild: tracker blockers
      // match on the vendor name, and that is a fine outcome, not a fault.
      console.warn("branch_load_failed", describe(err));
      return null;
    })
    .then((state) => {
      resolvedState = state;
      return state;
    });

  return statePromise;
}

/**
 * The in-app path the referring Branch link asked us to open, or null.
 *
 * Resolves with `initBranch`, so a caller that may run before boot settles
 * awaits this rather than reading the synchronous accessor below.
 */
export function branchDeepLinkPath(): Promise<string | null> {
  if (!statePromise) return Promise.resolve(null);
  return statePromise.then((state) => state?.deepLinkPath ?? null);
}

/** True once a Branch session exists. Used to decide whether to mint links. */
export function isBranchReady(): boolean {
  return resolvedState !== null;
}

/**
 * Mint a Branch link. Resolves to null when Branch is unavailable, so every
 * caller must already hold a working non-Branch destination to fall back to.
 */
export function createBranchLink(payload: BranchLinkPayload): Promise<string | null> {
  if (!statePromise) return Promise.resolve(null);
  return statePromise
    .then(
      (state) =>
        new Promise<string | null>((resolve) => {
          if (!state) {
            resolve(null);
            return;
          }
          state.client.link({ ...payload }, (err, url) => {
            if (err || !url) {
              console.warn(
                "branch_link_failed",
                payload.feature,
                err ? describe(err) : "empty_url",
              );
              resolve(null);
              return;
            }
            resolve(url);
          });
        }),
    )
    .catch(() => null);
}

/**
 * Record an install-intent event against the Branch session.
 *
 * `data` carries our own campaign constants only. The no-PHI rule is enforced
 * by the allowlist in `analytics-branch.ts`, which is the only caller.
 */
export function logBranchEvent(name: string, data: Record<string, unknown> = {}): void {
  if (!statePromise) return;
  void statePromise
    .then((state) => {
      state?.client.logEvent(name, data, (err) => {
        if (err) console.warn("branch_event_failed", name, describe(err));
      });
    })
    .catch(() => undefined);
}

/**
 * Clear Branch's first-party session.
 *
 * Shared-browser hygiene, and the same reason `reset-local-state.ts` exists:
 * Branch persists its session in localStorage, so without this the next member
 * to sign in on a kiosk or a shared desktop inherits the previous member's
 * attribution session and any install it later produces is credited to them.
 */
export function resetBranch(): void {
  if (!statePromise) return;
  void statePromise
    .then((state) => {
      state?.client.logout((err) => {
        if (err) console.warn("branch_logout_failed", describe(err));
      });
    })
    .catch(() => undefined);
}

/** Test-only reset of the module-level session cache. */
export function __resetBranchForTests(): void {
  statePromise = null;
  resolvedState = null;
}

/**
 * Branch hands back plain objects as often as Errors. Reduce either to a short
 * string so the reason is loggable without risking an object of unknown shape
 * reaching the log context.
 */
function describe(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  if (err && typeof err === "object" && "message" in err) {
    const message = (err as { message: unknown }).message;
    if (typeof message === "string") return message;
  }
  return "unknown";
}
